// TODO: Fix eslint suppression
/* eslint-disable accessor-pairs */
// @flow

import { mobileCheck, price } from './Helpers';
import PictureMedia from './PictureMedia';

export default class Product {
  #product: HTMLElement;
  #baseUrl: string;
  #image: HTMLElement;
  #title: HTMLElement;
  #rating: HTMLElement;
  #price: HTMLElement;
  #maxPrice: ?HTMLElement;
  #originalPrice: ?HTMLElement;
  #originalMaxPrice: ?HTMLElement;
  #promotionLabel: HTMLElement;
  #thumbnails: ?HTMLElement;
  #unitPrice: ?HTMLElement;
  #addToCartButton: ?HTMLButtonElement;

  /**
   * Create a new product given to the given template so we can easily modify it
   */
  constructor(element: HTMLElement) {
    this.#product = element;

    let baseUrl;
    if (this.#product.tagName === 'A') {
      baseUrl = this.#product.getAttribute('href');
    } else {
      baseUrl = this.#product.querySelector('a').getAttribute('href');
    }

    if (baseUrl) {
      this.#baseUrl = baseUrl;
    }

    const image: HTMLElement | null = this.#product.getElementByRef('picture');
    if (image) {
      this.#image = image;
    } else {
      throw new Error('image is required');
    }

    const title: HTMLElement | null = this.#product.getElementByRef('title');
    if (title) {
      this.#title = title;
    } else {
      throw new Error('Title is required');
    }

    const rating: HTMLElement | null = this.#product.getElementByRef('rating');
    if (rating) {
      this.#rating = rating;
    } else {
      throw new Error('Rating is required');
    }

    const price: HTMLElement | null = this.#product.getElementByRef('price');
    if (price) {
      this.#price = price;
    } else {
      throw new Error('Price is required');
    }

    this.#maxPrice = this.#product.getElementByRef('max-price');
    this.#originalPrice = this.#product.getElementByRef('original-price');
    this.#originalMaxPrice = this.#product.getElementByRef('original-max-price');
    this.#unitPrice = this.#product.getElementByRef('unit-price');

    const promotionLabel: HTMLElement | null = this.#product.getElementByRef('promotion-label');
    if (promotionLabel) {
      this.#promotionLabel = promotionLabel;
    }

    this.#thumbnails = this.#product.getElementByRef('thumbnails');

    this.#addToCartButton = this.#product.getElementByRef('add-to-cart');
  }

  /**
   * Get the base url of the product
   */
  get baseUrl(): string {
    return this.#baseUrl;
  }

  /**
   * Set the slug of the product
   */
  set slug(link: string): void {
    this.href = `${this.#baseUrl}/${link}`;
  }

  /**
   * Set the href of the product
   */
  set href(link: string): void {
    const aTag = this.#product.getElementsByTagName('a')[0];
    if (aTag) {
      aTag.setAttribute('href', `${link}`);
    } else {
      this.#product.setAttribute('href', `${link}`);
    }
  }

  /**
   * Set the sku of the product
   */
  set sku(sku: string): void {
    this.#product.dataset.sku = sku;
  }

  /**
   * Get the image element of the product
   */
  get image(): HTMLElement {
    return this.#image;
  }

  /**
   * Set the image of the product
   */
  set image(image: Object): void {
    this.#image.innerHTML = new PictureMedia(this.#image, image).build().innerHTML;
  }

  /**
   * Get the title element of the product
   */
  get title(): HTMLElement {
    return this.#title;
  }

  /**
   * Set the title of the product
   */
  set title(value: string) {
    this.#title.innerText = value;
  }

  /**
   * Get the rating wrapper element of the product
   */
  get rating(): HTMLElement {
    return this.#rating;
  }

  /**
   * Set the score of the product
   */
  set rating(rating: Object): void {
    const stars: NodeList<HTMLElement> = this.#rating.getElementsByRef('star');
    if (stars) {
      const len: number = stars.length;
      for (let i = 0; i < len; i++) {
        const star: HTMLElement = stars[i];
        if (rating.average > i) {
          const icon = star.getElementByRef('icon');
          icon.classList.add('text-secondary');
        }
      }
    }

    const count: HTMLElement | null = this.#rating.getElementByRef('count');
    if (count) {
      count.innerText = rating.count;
    }
  }

  /**
   * Get the price element of the product
   */
  get price(): HTMLElement {
    return this.#price;
  }

  /**
   * Set the price of the product
   */
  set price(number: number): void {
    if (number) {
      this.#price.innerText = price(number);
    } else {
      this.#price.innerText = window.store.getTranslation('product.discontinued');
    }
  }

  /**
   * Get the maximum price element of the product
   */
  get maxPrice(): HTMLElement | null {
    return this.#maxPrice;
  }

  /**
   * Set the maximum price of the product if it has one
   */
  set maxPrice(number: number): void {
    if (this.#maxPrice) {
      this.#maxPrice.innerText = price(number);
    }
  }

  /**
   * Get the original price element of the product
   */
  get originalPrice(): ?HTMLElement {
    return this.#originalPrice;
  }

  /**
   * Set the original price of the product if it has one
   */
  set originalPrice(number: number): void {
    if (!this.#originalPrice) return;
    this.#originalPrice.innerText = price(number);
  }

  /**
   * Get the original max price element of the product
   */
  get originalMaxPrice(): ?HTMLElement {
    return this.#originalMaxPrice;
  }

  /**
   * Set the original price of the product if it has one
   */
  set originalMaxPrice(number: number): void {
    if (!this.#originalMaxPrice) return;
    this.#originalMaxPrice.innerText = price(number);
  }

  /**
   *
   */
  get thumbnails(): ?HTMLElement {
    return this.#thumbnails;
  }

  /**
   * Set a reference attribute on the product
   */
  set ref(value: string): void {
    this.#product.setAttribute('ref', value);
  }

  /**
   * Set classes on the product
   */
  set class(value: string): void {
    this.#product.classList.add(...value.split(' '));
  }

  /**
   * Getter for the unit price element
   */
  get unitPrice(): ?HTMLElement {
    return this.#unitPrice;
  }

  /**
   * Set the label of the product if it has one
   */
  set promotionLabel(label: ?string): void {
    if (!this.#promotionLabel) return;

    if (!label) {
      this.#promotionLabel.remove();
    }

    this.#promotionLabel.innerText = label;
  }

  /**
   * Get the wrapper element of the product
   */
  get(): HTMLElement {
    return this.#product;
  }

  /**
   * Set the event data of the product if possible
   *
   * TODO: Remove the old datalayer data when migrated to GA4.
   */
  setEventData(data: Object, dataLayerData: Object, insiderVariationId: number | null, location: string | null): void {
    const eventAccessor: HTMLElement | null = this.#product.getElementByRef('event-accessor');
    if (!eventAccessor) return;
    eventAccessor.dataset.eventData = JSON.stringify({ product: dataLayerData });
    const innerProduct = this.#product.getElementByRef('inner-product');
    if (!innerProduct) return;

    const isSimpleProduct = !!this.#addToCartButton;

    innerProduct.dataset.eventData = JSON.stringify({
      sku: isSimpleProduct ? data.sku : undefined,
      title: isSimpleProduct ? data.title : undefined,
      item_id: isSimpleProduct ? data.parent_sku : data.sku,
      item_name: isSimpleProduct ? data.parent_title : data.title,
      category: data.category || dataLayerData.category,
      brand: data.brand || dataLayerData.brand,
      price: isSimpleProduct ? data.price_ex_vat : dataLayerData.price,
      list: dataLayerData.list,
      variationId: insiderVariationId,
      quantity: 1,
      location: location
    });

    if (!this.#addToCartButton) return;
    this.#addToCartButton.parentElement.dataset.eventData = JSON.stringify({ product: dataLayerData, quantity: 1 });
  }

  /**
   * Set the original price of the product if it has one
   */
  setUnitPrice(unitPrice: number, unit: string): void {
    if (!this.#unitPrice) return;
    this.#unitPrice.innerText = `(${price(unitPrice)} / ${unit})`;
  }

  /**
   * Remove the unit price from the DOM
   */
  removeUnitPrice(): void {
    if (this.#unitPrice) {
      this.#unitPrice.remove();
      this.#unitPrice = null;
    }
  }

  /**
   * Remove the maximum price element from the product
   */
  removeMaxPrice(): void {
    if (this.#maxPrice) {
      this.#maxPrice.remove();
      this.#maxPrice = null;
      const separator: HTMLElement | null = this.#product.getElementByRef('price-separator');
      if (separator) {
        separator.remove();
      }
    }
  }

  /**
   * Remove the original price element from the product
   */
  removeOriginalPrice(): void {
    if (!this.#originalPrice) return;
    this.#originalPrice.parentElement.remove();
    this.#originalPrice = null;
  }

  /**
   * Remove the original maximum price element from the product
   */
  removeOriginalMaxPrice(): void {
    if (!this.#originalMaxPrice) return;
    this.#originalMaxPrice.remove();
    this.#originalMaxPrice = null;

    const separator: HTMLElement | null = this.#product.getElementByRef('original-price-separator');
    if (!separator) return;
    separator.remove();
  }

  /**
   * Set the class of the outer element
   */
  removeOuterClass(className: string): void {
    this.#product.firstElementChild.classList.remove(className);
  }

  /**
   * Remove the repeatLabel
   */
  removeRepeatLabel(): void {
    const repeatLabel: HTMLElement | null = this.#product.getElementByRef('repeat-label');

    if (!repeatLabel) return;
    repeatLabel.remove();
  }

  /**
   * Update the add to cart button so the component can be instantiated
   *
   * TODO: Remove the old datalayer data when migrated to GA4.
   */
  updateAddToCart(data: Object, dataLayerData: Object, location: string | null): void {
    if (!this.#addToCartButton || !this.#addToCartButton.parentElement || !this.#addToCartButton.parentElement.parentElement) return;

    this.#addToCartButton.dataset.sku = data.sku;

    this.#addToCartButton.parentElement.dataset.eventData = JSON.stringify({
      sku: data.sku,
      title: data.title,
      item_id: data.parent_sku,
      item_name: data.parent_title,
      category: data.category,
      brand: data.brand,
      price: data.price_ex_vat,
      quantity: 1,
      list: dataLayerData.list,
      subscription_period: null,
      subscription_selection: data.subscription ? true : null,
      location: location
    });

    this.#addToCartButton.parentElement.parentElement.dataset.eventData = JSON.stringify({
      product: dataLayerData,
      quantity: 1
    });
  }

  addInsiderInformation(sku: string, url: string): void {
    const productDiv = this.#product.firstElementChild;
    if (!productDiv) return;
    const aTag = productDiv.getElementsByTagName('a')[0];
    const innerProduct = productDiv.getElementByRef('inner-product');

    if (innerProduct) {
      innerProduct.removeAttribute('data-on');
      innerProduct.removeAttribute('data-should-queue');
      innerProduct.removeAttribute('data-event-data');
      innerProduct.removeAttribute('data-ga4');
      innerProduct.removeAttribute('data-component');
    }
    productDiv.removeAttribute('data-on');
    productDiv.removeAttribute('data-should-queue');
    productDiv.removeAttribute('data-event-data');
    productDiv.removeAttribute('data-component');

    this.#product.classList.add(mobileCheck() ? 'ins-mobile-web-smart-recommender-box-item' : 'ins-web-smart-recommender-box-item');
    productDiv.classList.add('ins-product-box', 'ins-element-link', 'ins-add-to-cart-wrapper', 'ins-sr-api', 'hover:no-underline');
    productDiv.setAttribute('data-redirection-url', url);
    productDiv.setAttribute('ins-product-id', sku);
    productDiv.setAttribute('event-collection', 'true');
    productDiv.appendChild(aTag.firstElementChild);
    aTag.remove();
  }
}
